import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import aboutUsImg from './assets/about-us.jpg';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FlagIcon from '@mui/icons-material/Flag';
import VerifiedIcon from '@mui/icons-material/Verified';
import BalanceIcon from '@mui/icons-material/Balance';
import HandshakeIcon from '@mui/icons-material/Handshake';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import BuildIcon from '@mui/icons-material/Build';
import SecurityIcon from '@mui/icons-material/Security';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import LinkIcon from '@mui/icons-material/Link';

const AboutUs = () => {
  const { t } = useTranslation();
  const specializationImageUrl = 'https://lh3.googleusercontent.com/aida-public/AB6AXuAc1uZtYJPA8plwH48jiN6Ek5qjVq19nDDqkgHjyi4fc57AlliNcxA70rDxN488mjpGNh3kPIXH3by9qOJbYmHwc3SCyTHNo1EEp28YxWb3Zh0A03UYSj01paqIi6o324DkHAC9W7aIXRaQ0sxjFuvhnzMm_Nq7NKVYlFA9JS0XVT4Es7DkXIY0ESlgm29tnXQN8XfoOYvG241kngqpi62i7wrNXp4cjmUbR8aZYtnAn-_h1i3uK9rnxQ2aNdUJzlofz5QvP6ZOAtw';
  const [showContent, setShowContent] = useState(false);
  const [showValues, setShowValues] = useState(false);
  const valuesRef = useRef(null);
  const [showVision, setShowVision] = useState(false);
  const [showMission, setShowMission] = useState(false);
  const visionRef = useRef(null);
  const missionRef = useRef(null);
  const [showSpecialization, setShowSpecialization] = useState(false);
  const specializationRef = useRef(null);

  const coreValues = [
    {
      key: 'integrity',
      title: t('about.integrity.title'),
      desc: t('about.integrity.description'),
    },
    {
      key: 'innovation',
      title: t('about.innovation.title'),
      desc: t('about.innovation.description'),
    },
    {
      key: 'customer',
      title: t('about.customerFocus.title'),
      desc: t('about.customerFocus.description'),
    },
    {
      key: 'excellence',
      title: t('about.excellence.title'),
      desc: t('about.excellence.description'),
    },
  ];

  const coreValueIcons = {
    integrity: <VerifiedIcon className="!h-10 !w-10 text-primary" />,
    innovation: <BalanceIcon className="!h-10 !w-10 text-primary" />,
    customer: <HandshakeIcon className="!h-10 !w-10 text-primary" />,
    excellence: <LightbulbIcon className="!h-10 !w-10 text-primary" />,
  };

  const specializationItems = [
    {
      icon: <BuildIcon className="!h-6 !w-6 text-blue-300" />,
      title: t('specializations.welding.title'),
      desc: t('specializations.welding.description'),
    },
    {
      icon: <SecurityIcon className="!h-6 !w-6 text-blue-300" />,
      title: t('specializations.hardware.title'),
      desc: t('specializations.hardware.description'),
    },
    {
      icon: <PrecisionManufacturingIcon className="!h-6 !w-6 text-blue-300" />,
      title: t('specializations.powerTools.title'),
      desc: t('specializations.powerTools.description'),
    },
    {
      icon: <LinkIcon className="!h-6 !w-6 text-blue-300" />,
      title: t('specializations.abrasive.title'),
      desc: t('specializations.abrasive.description'),
    },
    {
      icon: <SecurityIcon className="!h-6 !w-6 text-blue-300" />,
      title: t('specializations.ppe.title'),
      desc: t('specializations.ppe.description'),
    },
    {
      icon: <BuildIcon className="!h-6 !w-6 text-blue-300" />,
      title: t('specializations.repair.title'),
      desc: t('specializations.repair.description'),
    },
  ];

  useEffect(() => {
    setTimeout(() => setShowContent(true), 150);

    // Core Values animation
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowValues(true);
        }
      },
      { threshold: 0.3 }
    );
    if (valuesRef.current) {
      observer.observe(valuesRef.current);
    }

    // Vision animation
    const visionObs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setShowVision(true);
      },
      { threshold: 0.2 }
    );
    if (visionRef.current) visionObs.observe(visionRef.current);

    // Mission animation
    const missionObs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setShowMission(true);
      },
      { threshold: 0.2 }
    );
    if (missionRef.current) missionObs.observe(missionRef.current);

    // Specialization animation
    const specializationObs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setShowSpecialization(true);
      },
      { threshold: 0.2 }
    );
    if (specializationRef.current) specializationObs.observe(specializationRef.current);

    return () => {
      observer.disconnect();
      visionObs.disconnect();
      missionObs.disconnect();
      specializationObs.disconnect();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Animated background shapes */}
      <div className="absolute -top-32 -left-32 w-[500px] h-[500px] bg-primary/20 rounded-full blur-3xl animate-pulse z-0" />
      <div className="absolute top-1/2 -right-40 w-[400px] h-[400px] bg-blue-200/30 rounded-full blur-2xl animate-pulse z-0" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[200px] bg-primary/10 rounded-full blur-2xl animate-pulse z-0" />
      {/* Hero Section */}
      <section className="relative flex min-h-[360px] md:min-h-[500px] md:h-[614px] w-full items-center justify-center overflow-hidden bg-gray-900">
        <div className="absolute inset-0 z-10 bg-black/60" />
        <img
          src={aboutUsImg}
          alt={t('about.heroImageAlt')}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center grayscale-[20%]"
        />
        <div className="relative z-20 mx-auto max-w-7xl px-4 text-center md:px-8">
          <h1 className="mb-4 text-2xl font-bold uppercase tracking-tight text-white drop-shadow-sm sm:text-3xl md:text-6xl">
            {t('about.heroTitle')}
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-gray-200 md:text-xl">
            {t('about.heroSubtitle')}
          </p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="relative z-10 bg-white py-16 px-4">
        <div className="mx-auto max-w-7xl">
          <div
            className={`mx-auto w-full max-w-3xl text-center transition-all duration-700 ${
              showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h2 className="mb-4 text-3xl font-bold text-primary md:text-4xl">{t('about.ourLegacy')}</h2>
            <div className="mx-auto mb-6 h-1 w-16 bg-blue-400" />
            <p className="text-base leading-relaxed text-gray-700 md:text-xl">
              {t('about.missionText')}
            </p>
          </div>
        </div>
      </section>

      <main className="w-full px-2 md:px-4 flex flex-col items-center text-left relative z-10">
        <div className="max-w-4xl w-full mx-auto">
          <p className="text-base md:text-lg text-gray-600 mb-8 md:mb-12">
            <strong>{t('about.companyName')}</strong> {t('about.description')}
          </p>
        </div>
        {/* Vision & Mission Section */}
        <section className="w-full border-y-2 border-gray-200 bg-gray-100 py-16">
          <div className="w-full px-4 md:px-8">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div
                ref={visionRef}
                className={`rounded-lg md:ml-40 border border-gray-200 bg-white p-6 shadow-md transition-all duration-700 hover:shadow-xl ${
                  showVision ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
                }`}
              >
                <div className="mb-4 flex items-center gap-2 text-primary">
                  <VisibilityIcon className="!w-7 !h-7 text-primary" />
                  <h3 className="text-2xl font-bold text-primary">{t('about.vision')}</h3>
                </div>
                <p className="text-gray-700">
                  {t('about.visionText')}
                </p>
              </div>

              <div
                ref={missionRef}
                className={`rounded-lg md:mr-40 border border-gray-200 bg-white p-6 shadow-md transition-all duration-700 hover:shadow-xl ${
                  showMission ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
                }`}
              >
                <div className="mb-4 flex items-center gap-2 text-primary">
                  <FlagIcon className="!w-7 !h-7 text-primary" />
                  <h3 className="text-2xl font-bold text-primary">{t('about.mission')}</h3>
                </div>
                <p className="text-gray-700">
                  {t('about.missionText')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values Section */}
        <section ref={valuesRef} className="w-full bg-white py-16 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <h2 className={`mb-10 text-center text-3xl font-bold text-primary md:text-4xl transition-all duration-700 ${showValues ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              {t('about.coreValues')}
            </h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              {coreValues.map((value, idx) => (
                <div
                  key={value.key}
                  className={`border-t-4 border-blue-300 bg-gray-50 p-6 transition-colors duration-200 hover:bg-gray-100 ${
                    showValues ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${0.1 + idx * 0.1}s` }}
                >
                  <div className="mb-3 block">
                    {coreValueIcons[value.key]}
                  </div>
                  <h4 className="mb-2 text-xl font-bold text-primary">{value.title}</h4>
                  <p className="text-sm text-gray-700">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Specializations Section */}
      <section ref={specializationRef} className="relative z-10 bg-gray-900 py-16 text-white">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="flex flex-col items-center gap-10 md:flex-row">
            <div
              className={`w-full md:w-1/2 transition-all duration-700 ${
                showSpecialization ? 'translate-x-0 opacity-100' : '-translate-x-8 opacity-0'
              }`}
            >
              <h2 className="mb-4 text-3xl font-bold text-gray-100 md:text-4xl">{t('home.specializations')}</h2>
              <p className="mb-6 text-lg text-gray-300">
                {t('about.specializationsSubtitle')}
              </p>
              <ul className="space-y-3">
                {specializationItems.map((item) => (
                  <li key={item.title} className="border-b border-gray-700 pb-3">
                    <div className="mb-1 flex items-center gap-3">
                      {item.icon}
                      <span className="text-base font-semibold">{item.title}</span>
                    </div>
                    <p className="pl-9 text-sm text-gray-300">{item.desc}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div
              className={`w-full md:w-1/2 transition-all duration-700 ${
                showSpecialization ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0'
              }`}
            >
              <div className="rounded border border-gray-600 bg-gray-700 p-2">
                <img
                  src={specializationImageUrl}
                  alt={t('about.specializationImageAlt')}
                  className="aspect-square w-full rounded-sm object-cover grayscale-[10%]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default AboutUs; 